import { QuestionType, UserTaggingQuestion } from './types';
import { useState } from 'react';
import _ from 'lodash';
import {
  createUserTagging,
  updateUserTagging,
  deleteUserTagging,
  createCustomUserTagging,
  createFreeformUserTagging,
  createRIUserTagging,
} from './recordSurvey';
import { numberMap } from './types';
import { hasCustomAnswer, makeUpdater, useElapsedTime } from './helpers';
import { useResearchInterests } from 'js/videos/shared/research_interest_tags/ResearchInterestTagsInput';

export class TagCache {
  protected tags: { [key: string]: string[] };

  constructor() {
    this.tags = {};
  }

  public hasTag(tagType: string, tagText: string): boolean {
    if (this.tags[tagType] === undefined) return false;
    if (this.tags[tagType].length === 0) return false;
    return this.tags[tagType].some((tag) => tag === tagText);
  }

  // TODO: I suspect this isn't necessary but changing it requires asking a PM
  public hasSingularTag(tagType: string, tagText: string): boolean {
    if (this.tags[tagType] === undefined) return false;
    if (this.tags[tagType].length !== 1) return false;
    return this.tags[tagType][0] === tagText;
  }

  public isFaculty(): boolean {
    return this.hasTag('UserTaggings::UserRole', 'faculty');
  }

  public isGradStudent(): boolean {
    return this.hasTag('UserTaggings::UserRole', 'graduate');
  }

  public isProfessional(): boolean {
    return this.hasTag('UserTaggings::UserRole', 'profession');
  }

  public hasPublishedArticle(): boolean {
    return this.hasTag('UserTaggings::UserAuthorship', 'academic');
  }

  public hasPublishedThesis(): boolean {
    return this.hasTag('UserTaggings::UserAuthorship', 'gradStudent');
  }

  public topicArea(): string {
    const topics = this.tags['UserTaggings::UserTopic'];
    return topics ? topics[topics.length - 1] : '';
  }
}

class WritableTagCache extends TagCache {
  public addTag(tagType: string, tagText: string) {
    if (this.tags[tagType] === undefined) {
      this.tags[tagType] = [];
    }
    this.tags[tagType].push(tagText);
  }

  public removeTag(tagType: string, tagText: string) {
    if (this.tags[tagType] === undefined) return;
    this.tags[tagType] = this.tags[tagType].filter((text) => text !== tagText);
  }

  public clearTagType(tagType: string) {
    delete this.tags[tagType];
  }
}

export const useSurveyState = (questions: UserTaggingQuestion[]) => {
  // custom answer for each question
  const [customAnswers, setCustomAnswers] = useState<(string | null)[]>(
    questions.map((_) => null),
  );

  // for each question, array of answer numbers that are currently selected
  const [selectedAnswers, setSelectedAnswers] = useState<number[][]>(
    questions.map((_) => []),
  );

  // for each question, map from question number to ID of entry in UserTaggings table
  const [ids, setIds] = useState<numberMap[]>(
    questions.map((_) => {
      return {};
    }),
  );

  const [taggings, setTaggings] = useState<WritableTagCache>(
    new WritableTagCache(),
  );

  const riState = useResearchInterests([]);

  const submitRIanswers = (source: string) => {
    const { researchInterests } = riState;
    createRIUserTagging(
      'UserTaggings::UserTopic',
      researchInterests.map(({ displayName, id }) => ({
        name: displayName,
        id,
      })),
      source,
    );
  };

  /** For each freeform question, hash from the question prompt to array of answer strings */
  const [freeformAnswers, setFreeformAnswers] = useState<
    Record<string, string[]>
  >(
    Object.fromEntries(
      questions
        .filter((q) => q.questionType === QuestionType.Freeform)
        .map((q) => [q.prompt, Array<string>(q.maxAnswers || 1).fill('')]),
    ),
  );

  const updateFreeformAnswer = (prompt: string, i: number, answer: string) => {
    setFreeformAnswers(
      makeUpdater((copy) => {
        copy[prompt][i] = answer;
      }),
    );
  };

  /** Submit the answers for the freeform question of the given prompt */
  const submitFreeformAnswers = (source: string, qPrompt: string) => {
    const answers = freeformAnswers[qPrompt];
    const question = questions.find((q) => q.prompt === qPrompt);
    if (question === undefined)
      throw new Error('Prompt does not correspond to question.');
    createFreeformUserTagging(
      question.userTaggingType,
      answers.filter((a) => a.length > 0),
      source,
    );
  };

  // elapsed time functions
  const { questionElapsedTime, getSetQuestionLoadTime } =
    useElapsedTime(questions);

  // selection logic for each question type:

  const singleSelectAnswer = (questionIndex: number, answerIndex: number) => {
    const currentQuestion = questions[questionIndex];
    if (
      currentQuestion.questionType !== QuestionType.SingleSelect &&
      currentQuestion.questionType !== QuestionType.SingleSelectColumns
    )
      throw new Error('Incorrect question type');
    if (
      hasCustomAnswer(currentQuestion) &&
      answerIndex !== currentQuestion.userTags.length - 1
    ) {
      setCustomAnswers(
        makeUpdater((copy) => {
          copy[questionIndex] = null;
        }),
      );
    }

    // visual logic
    setSelectedAnswers(
      makeUpdater((copy) => {
        copy[questionIndex] = [answerIndex];
      }),
    );

    // elapsed time logic
    const elapsedTime = questionElapsedTime(questionIndex);

    // user tagging logic
    const tagType = currentQuestion.userTaggingType;
    const tagID = currentQuestion.userTags[answerIndex].userTagId;
    const tagText = currentQuestion.userTags[answerIndex].userTagText;
    taggings.clearTagType(tagType);
    taggings.addTag(tagType, tagText);
    setTaggings(taggings);

    if (tagType === 'UserTaggings::UserTopic') {
      createRIUserTagging(
        'UserTaggings::UserTopic',
        [{ name: tagText, id: tagID }],
        'sutd-v3',
      );
    } else {
      const id = ids[questionIndex][0];
      if (!id) {
        setIds(
          makeUpdater((copy) => {
            copy[questionIndex][0] = -1;
          }),
        );

        createUserTagging(tagType, tagID, elapsedTime)
          .then((r) => r.json())
          .then((data) =>
            setIds(
              makeUpdater((copy) => {
                copy[questionIndex][0] = data.tagging_id;
              }),
            ),
          );
      } else if (id === -1) {
        return null;
      } else {
        updateUserTagging(id, tagID, elapsedTime);
      }
    }
  };

  const multiSelectAnswer = (questionIndex: number, answerIndex: number) => {
    // visual logic
    setSelectedAnswers(
      makeUpdater((copy) => {
        const answerArray = copy[questionIndex];
        const alreadySelected = answerArray.includes(answerIndex);
        if (alreadySelected) {
          _.remove(answerArray, (x) => x === answerIndex);
        } else {
          answerArray.push(answerIndex);
        }
      }),
    );

    // elapsed time logic
    const elapsedTime = questionElapsedTime(questionIndex);

    // user tagging logic
    const currentQuestion = questions[questionIndex];
    if (currentQuestion.questionType !== QuestionType.MultiSelect)
      throw new Error('Incorrect question type');
    const tagType = currentQuestion.userTaggingType;
    const tagID = currentQuestion.userTags[answerIndex].userTagId;
    const tagText = currentQuestion.userTags[answerIndex].userTagText;
    const taggingId = ids[questionIndex][answerIndex];

    if (!taggingId) {
      // add tag
      taggings.addTag(tagType, tagText);
      setIds(
        makeUpdater((copy) => {
          copy[questionIndex][answerIndex] = -1;
        }),
      );
      createUserTagging(tagType, tagID, elapsedTime)
        .then((r) => r.json())
        .then((data) =>
          setIds(
            makeUpdater((copy) => {
              copy[questionIndex][answerIndex] = data.tagging_id;
            }),
          ),
        );
    } else if (taggingId === -1) {
      return null;
    } else {
      // remove tag
      taggings.removeTag(tagType, tagText);
      deleteUserTagging(taggingId);
      setIds(
        makeUpdater((copy) => {
          copy[questionIndex][answerIndex] = undefined;
        }),
      );
    }
    setTaggings(taggings);
  };

  const selectAnswer = (questionIndex: number, answerIndex: number) => {
    const questionType = questions[questionIndex].questionType;
    switch (questionType) {
      case QuestionType.SingleSelect:
        return singleSelectAnswer(questionIndex, answerIndex);
      case QuestionType.SingleSelectColumns:
        return singleSelectAnswer(questionIndex, answerIndex);
      case QuestionType.MultiSelect:
        return multiSelectAnswer(questionIndex, answerIndex);
      default:
        return null;
    }
  };

  const questionAnswered = (questionIndex: number) => {
    const question = questions[questionIndex];
    switch (question.questionType) {
      case QuestionType.Freeform:
        return freeformAnswers[question.prompt].some(
          (answer) => answer.length > 0,
        );
      case QuestionType.RI:
        return riState.researchInterests.length > 0;
    }
    const hasCustomText = question.userTags
      .map((tag) => tag.userTagText)
      .includes('other');
    const selected = selectedAnswers[questionIndex];
    const lastAnswerIndex = question.userTags.length - 1;
    if (hasCustomText && _.isEqual(selected, [lastAnswerIndex])) {
      return !!(
        customAnswers[questionIndex] &&
        customAnswers[questionIndex]?.trim() !== ''
      );
    }
    return !_.isEmpty(selected);
  };

  const customAnswerSetter = (questionIndex: number, text: string) => {
    setCustomAnswers(
      makeUpdater((copy) => {
        copy[questionIndex] = text;
      }),
    );
  };

  const submitCustomAnswers = () => {
    customAnswers.map((text: null | string, index: number) => {
      const question = questions[index];
      if (
        question.questionType !== QuestionType.Freeform &&
        text &&
        text.trim() !== ''
      ) {
        createCustomUserTagging(question.userTaggingType, text.trim());
      }
    });
  };

  return {
    selectedAnswers,
    selectAnswer,
    questionAnswered,
    getSetQuestionLoadTime,
    customAnswerSetter,
    submitCustomAnswers,
    customAnswers,
    freeformAnswers,
    updateFreeformAnswer,
    submitFreeformAnswers,
    riState,
    submitRIanswers,
    taggings,
  };
};
